import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Users, Lock, Globe, Briefcase, Heart, Sparkles, MessageCircle, Plus,
  Rss, UserPlus, Send, Loader2, Settings, X, Check, Trash2, ArrowLeft, ShieldCheck,
  CalendarDays, Wrench, Image as ImageIcon, Video, Search, Link2, Copy, ChevronLeft, ExternalLink,
} from "lucide-react";
import PhotoDrop, { fileToDataUrl } from "@/components/PhotoDrop";

const TYPE_ICON = { work: Briefcase, friends: Heart, community: Sparkles };

/**
 * GroupsShell — Telegram-inspired layout:
 *   ┌────────────────┬─────────────────────────────────────┐
 *   │ Groups sidebar │ Selected group: header + tab panels │
 *   └────────────────┴─────────────────────────────────────┘
 * On mobile the sidebar and pane stack: /groups shows the sidebar, /groups/:id shows the pane.
 */
export default function GroupsShell() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [groups, setGroups] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const loadGroups = useCallback(async () => {
    try {
      const { data } = await api.get(`/groups${filter ? `?type=${filter}` : ""}`);
      setGroups(data || []);
    } catch (e) { toast.error(formatErr(e)); }
  }, [filter]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const showSidebarOnMobile = !id;

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-4 lg:py-6" data-testid="groups-shell">
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 min-h-[80vh]">
        <aside className={`${showSidebarOnMobile ? "block" : "hidden"} lg:block card p-3 h-fit sticky top-4`} data-testid="groups-sidebar">
          <div className="flex items-center justify-between mb-3 px-1">
            <p className="overline">Groups</p>
            {user && (
              <Link to="/groups/new" className="btn-ghost !py-1 !px-2 text-xs" data-testid="groups-new-btn">
                <Plus size={12}/> New
              </Link>
            )}
          </div>
          <div className="flex gap-1 flex-wrap mb-2 px-1" data-testid="groups-type-filters">
            {[["", "All"], ["work", "Work"], ["friends", "Friends"], ["community", "Community"]].map(([t, l]) => (
              <button key={t} onClick={() => setFilter(t)}
                data-testid={`groups-filter-${t || "all"}`}
                className={`text-[11px] px-2 py-1 rounded-full ${filter===t ? "bg-[#0A0A0A] text-white" : "text-[#4A4A4A] hover:bg-[#F2F2F0]"}`}>
                {l}
              </button>
            ))}
          </div>
          <ul className="space-y-0.5" data-testid="groups-list">
            {groups.length === 0 && (
              <li className="p-4 text-xs text-[#8A8A8A]">No groups yet.
                {user && <> Start one with <Link to="/groups/new" className="text-[#0057FF]">New</Link>.</>}
              </li>
            )}
            {groups.map(g => (
              <li key={g.id}>
                <button
                  onClick={() => nav(`/groups/${g.id}`)}
                  className={`w-full flex items-center gap-2.5 p-2 rounded-xl text-left transition-colors ${id === g.id ? "bg-[#EEF3FF]" : "hover:bg-[#FAFAF8]"}`}
                  data-testid={`sidebar-group-${g.id}`}>
                  <GroupIcon g={g}/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{g.name}</p>
                    <p className="text-[11px] text-[#8A8A8A] inline-flex items-center gap-1">
                      {g.privacy === "private" ? <Lock size={9}/> : <Globe size={9}/>}
                      {g.member_count} · {g.type}
                      {Number(g.price_monthly) > 0 && <> · ${Number(g.price_monthly).toFixed(0)}/mo</>}
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className={`${showSidebarOnMobile ? "hidden" : "block"} lg:block min-w-0`} data-testid="groups-main">
          {id
            ? <GroupPane id={id} onGroupChanged={loadGroups}/>
            : <GroupsEmptyState canCreate={!!user}/>}
        </section>
      </div>
    </div>
  );
}

function GroupIcon({ g }) {
  const Icon = TYPE_ICON[g.type] || Sparkles;
  return g.avatar
    ? <img src={g.avatar} alt="" className="h-9 w-9 rounded-xl object-cover bg-[#E5E5E3] shrink-0"/>
    : <div className="h-9 w-9 rounded-xl bg-[#EEF3FF] text-[#0057FF] inline-flex items-center justify-center shrink-0"><Icon size={14}/></div>;
}

function GroupsEmptyState({ canCreate }) {
  return (
    <div className="card p-12 text-center h-full flex flex-col items-center justify-center" data-testid="groups-empty">
      <Users size={30} className="text-[#0057FF] mb-2"/>
      <h2 className="text-2xl font-heading">Pick a group on the left</h2>
      <p className="text-sm text-[#4A4A4A] mt-2 max-w-sm">Chat, share media, plan events and see what&apos;s booked — all in one place.</p>
      {canCreate && <Link to="/groups/new" className="btn-primary mt-5">Create a group</Link>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Group pane — header + tabs                                          */
/* ------------------------------------------------------------------ */
function GroupPane({ id, onGroupChanged }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [g, setG] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("chat");
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = useCallback(() => api.get(`/groups/${id}`)
    .then(r => { setG(r.data); setErr(""); onGroupChanged?.(); })
    .catch(e => setErr(formatErr(e))), [id, onGroupChanged]);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe return
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("paid") === "1" && sp.get("session_id")) {
      api.get(`/groups/subscribe/status/${sp.get("session_id")}`)
        .then(() => { toast.success("Welcome to the group!"); load(); }).catch(()=>{});
    }
  }, [load]);

  if (err) return (
    <div className="card p-10 text-center">
      <p className="text-[#B84B4B] font-medium">{err}</p>
      <button onClick={() => nav("/groups")} className="btn-ghost mt-4"><ArrowLeft size={14}/> Back</button>
    </div>
  );
  if (!g) return <div className="card p-16 text-center text-[#8A8A8A]"><Loader2 size={16} className="animate-spin inline-block"/> Loading…</div>;

  const canSeeContent = g.is_member || g.privacy === "public";
  const isPaid = Number(g.price_monthly) > 0;
  const TypeIcon = TYPE_ICON[g.type] || Sparkles;

  return (
    <div className="card overflow-hidden flex flex-col h-[calc(100vh-140px)] lg:h-[calc(100vh-100px)]" data-testid="group-pane">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-[#E5E5E3]" data-testid="group-header">
        <button onClick={() => nav("/groups")} className="lg:hidden h-8 w-8 rounded-full hover:bg-[#F2F2F0] inline-flex items-center justify-center" aria-label="Back">
          <ChevronLeft size={18}/>
        </button>
        <GroupIcon g={g}/>
        <div className="flex-1 min-w-0">
          <p className="text-lg font-heading truncate" data-testid="group-title">{g.name}</p>
          <p className="text-[11px] text-[#8A8A8A] flex items-center gap-1.5 flex-wrap">
            <TypeIcon size={10}/>{g.type}
            <span>·</span>
            {g.privacy === "private" ? <Lock size={10}/> : <Globe size={10}/>}{g.privacy}
            <span>·</span>
            <Users size={10}/> {g.member_count}
            {isPaid && <><span>·</span><span className="badge-soft">${Number(g.price_monthly).toFixed(0)}/mo</span></>}
          </p>
        </div>
        {g.is_admin && (
          <button onClick={() => setInviteOpen(true)}
                  className="btn-secondary text-xs" data-testid="group-invite-btn">
            <UserPlus size={12}/> Invite
          </button>
        )}
        <JoinControls g={g} onChanged={load}/>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-3 pt-2 border-b border-[#E5E5E3] bg-[#FAFAF8]" data-testid="group-tabs">
        {canSeeContent && <>
          <TabBtn active={tab==="chat"}     onClick={()=>setTab("chat")}     icon={<MessageCircle size={13}/>} label="Chat" testid="tab-chat"/>
          <TabBtn active={tab==="feed"}     onClick={()=>setTab("feed")}     icon={<Rss size={13}/>}          label="Feed" testid="tab-feed"/>
          <TabBtn active={tab==="calendar"} onClick={()=>setTab("calendar")} icon={<CalendarDays size={13}/>} label="Calendar" testid="tab-calendar"/>
          <TabBtn active={tab==="tools"}    onClick={()=>setTab("tools")}    icon={<Wrench size={13}/>}        label="Tools" testid="tab-tools"/>
          <TabBtn active={tab==="members"}  onClick={()=>setTab("members")}  icon={<Users size={13}/>}        label="Members" testid="tab-members"/>
        </>}
        {g.is_admin && <TabBtn active={tab==="manage"} onClick={()=>setTab("manage")} icon={<Settings size={13}/>} label="Manage" testid="tab-manage"/>}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden bg-white">
        {!canSeeContent && <LockedView g={g}/>}
        {canSeeContent && tab === "chat"     && <ChatTab gid={id}/>}
        {canSeeContent && tab === "feed"     && <FeedTab gid={id}/>}
        {canSeeContent && tab === "calendar" && <CalendarTab gid={id}/>}
        {canSeeContent && tab === "tools"    && <ToolsTab gid={id}/>}
        {canSeeContent && tab === "members"  && <MembersTab gid={id}/>}
        {g.is_admin && tab === "manage"      && <ManageTab g={g} onChanged={load}/>}
      </div>

      {inviteOpen && <InviteDialog gid={id} onClose={() => setInviteOpen(false)}/>}
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors
        ${active ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#8A8A8A] hover:text-[#0A0A0A]"}`}>
      {icon} {label}
    </button>
  );
}

function LockedView({ g }) {
  const isPaid = Number(g.price_monthly) > 0;
  return (
    <div className="p-10 text-center h-full flex flex-col items-center justify-center" data-testid="group-locked">
      <Lock size={26} className="text-[#0057FF] mb-3"/>
      <h3 className="text-xl font-heading">Members only.</h3>
      <p className="text-sm text-[#4A4A4A] mt-2 max-w-md">
        {isPaid
          ? `Subscribe for $${Number(g.price_monthly).toFixed(0)}/month to unlock the chat, calendar and tools.`
          : "Send a join request or ask an admin for an invite link."}
      </p>
    </div>
  );
}

function JoinControls({ g, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user) return <Link to="/login" className="btn-primary text-xs">Sign in</Link>;
  if (g.is_owner) return null;
  const isPaid = Number(g.price_monthly) > 0;
  const requestOrJoin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${g.id}/join_request`, { message: "" });
      if (data.joined) toast.success("You're in.");
      else if (data.already_member) toast("Already a member");
      else toast.success("Request sent — awaiting approval.");
      onChanged();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${g.id}/subscribe`);
      window.location.href = data.url;
    } catch (e) { toast.error(formatErr(e)); setBusy(false); }
  };
  const leave = async () => {
    if (!confirm("Leave this group?")) return;
    try { await api.post(`/groups/${g.id}/leave`); toast.success("Left"); onChanged(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (g.is_member) return <button onClick={leave} className="btn-ghost text-xs text-[#B84B4B]" data-testid="group-leave-btn">Leave</button>;
  if (g.pending_request) return <span className="text-xs text-[#8A8A8A]" data-testid="group-pending-badge">Request pending…</span>;
  if (isPaid) return <button onClick={subscribe} disabled={busy} className="btn-primary text-xs" data-testid="group-subscribe-btn">{busy ? <Loader2 size={12} className="animate-spin"/> : `Subscribe · $${Number(g.price_monthly).toFixed(0)}/mo`}</button>;
  return <button onClick={requestOrJoin} disabled={busy} className="btn-primary text-xs" data-testid="group-join-btn">{busy ? <Loader2 size={12} className="animate-spin"/> : <><UserPlus size={12}/>{g.privacy==="public" ? "Join" : "Request"}</>}</button>;
}

/* ---------- Chat tab with media upload ---------- */
function ChatTab({ gid }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [attach, setAttach] = useState(null); // {url, type}
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async (silent) => {
    try {
      const { data } = await api.get(`/groups/${gid}/messages`);
      setMsgs(data || []);
      if (!silent) requestAnimationFrame(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; });
    } catch { /* ignore */ }
  }, [gid]);
  useEffect(() => {
    load(false);
    const t = setInterval(() => load(true), 6000);
    return () => clearInterval(t);
  }, [load]);

  const uploadFile = async (file) => {
    if (!file) return;
    const isVideo = (file.type || "").startsWith("video/");
    setBusy(true);
    try {
      let data;
      if (isVideo) {
        // Videos still go via multipart (backend supports it)
        const fd = new FormData();
        fd.append("file", file);
        ({ data } = await api.post("/uploads/video", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        }));
      } else {
        // Images use base64 JSON to match backend UploadIn schema
        const dataUrl = await fileToDataUrl(file, 1600);
        ({ data } = await api.post("/uploads/image", { data_url: dataUrl }));
      }
      if (!data?.url) throw new Error("Upload succeeded but no URL was returned");
      // Ensure the URL is absolute so <img src> works everywhere (relative /api/files/… is
      // also fine because ingress routes /api/* to backend, but being explicit prevents
      // any browser origin surprises).
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const url = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      setAttach({ url, type: isVideo ? "video" : "image" });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !attach) return;
    setBusy(true);
    try {
      const payload = {
        text,
        image_url: attach?.type === "image" ? attach.url : "",
        video_url: attach?.type === "video" ? attach.url : "",
      };
      await api.post(`/groups/${gid}/messages/media`, payload);
      setText(""); setAttach(null);
      await load(false);
    } catch (e2) { toast.error(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const startVideoCall = async () => {
    const room = `hoomanz-group-${crypto.randomUUID().slice(0, 8)}`;
    const callUrl = `https://meet.jit.si/${room}#config.prejoinPageEnabled=false`;
    setBusy(true);
    try {
      await api.post(`/groups/${gid}/messages/media`, {
        text: "📹 Started a group video call — tap Join to hop in.",
        video_call_url: callUrl,
      });
      await load(false);
      window.open(callUrl, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="h-full flex flex-col" data-testid="group-chat">
      <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-2">
        {msgs.length === 0 && <p className="text-xs text-[#8A8A8A]">No messages yet. Say hi 👋</p>}
        {msgs.map(m => {
          const mine = m.from_user_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm break-words ${mine ? "bg-[#0057FF] text-white rounded-br-md" : "bg-[#F2F2F0] text-[#0A0A0A] rounded-bl-md"}`} data-testid={`chat-msg-${m.id}`}>
                {!mine && <p className="text-[10px] font-medium mb-0.5">{m.from?.name}</p>}
                {m.image_url && <img src={m.image_url} alt="" data-lightbox className="rounded-xl mb-1.5 max-w-full max-h-[280px] object-cover cursor-zoom-in"/>}
                {m.video_url && <video src={m.video_url} controls className="rounded-xl mb-1.5 max-w-full max-h-[320px]"/>}
                {m.text && <p className="whitespace-pre-wrap">{m.text}</p>}
                {m.video_call_url && (
                  <a href={m.video_call_url} target="_blank" rel="noopener noreferrer"
                     className={`mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${mine ? "bg-white text-[#0057FF]" : "bg-[#0057FF] text-white"} hover:opacity-90`}
                     data-testid={`chat-join-call-${m.id}`}>
                    <Video size={12}/> Join call <ExternalLink size={10}/>
                  </a>
                )}
                <p className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-[#8A8A8A]"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
            </div>
          );
        })}
      </div>
      {attach && (
        <div className="border-t border-[#E5E5E3] px-3 py-2 flex items-center gap-2" data-testid="chat-attach-preview">
          {attach.type === "image"
            ? <img src={attach.url} alt="" className="h-12 w-12 object-cover rounded-lg"/>
            : <div className="h-12 w-16 bg-black rounded-lg flex items-center justify-center text-white"><Video size={16}/></div>}
          <p className="text-xs text-[#4A4A4A] flex-1 truncate">Attachment ready · {attach.type}</p>
          <button onClick={() => setAttach(null)} className="btn-ghost !py-1 !px-2 text-xs" data-testid="chat-attach-remove"><X size={12}/></button>
        </div>
      )}
      <form onSubmit={send} className="border-t border-[#E5E5E3] p-2 flex items-center gap-1.5">
        <input type="file" ref={fileRef} accept="image/*,video/*" hidden onChange={(e)=>uploadFile(e.target.files?.[0])} data-testid="chat-file-input"/>
        <button type="button" onClick={()=>fileRef.current?.click()} className="btn-ghost !p-2" title="Attach photo or video" data-testid="chat-attach-btn">
          <ImageIcon size={16}/>
        </button>
        <button type="button" onClick={startVideoCall} className="btn-ghost !p-2" title="Start a video call" disabled={busy} data-testid="chat-start-call">
          <Video size={16}/>
        </button>
        <input value={text} onChange={(e)=>setText(e.target.value)} placeholder="Message the group…" className="input flex-1" data-testid="group-chat-input"/>
        <button type="submit" disabled={(!text.trim() && !attach) || busy} className="btn-primary !py-2 !px-3" data-testid="group-chat-send">
          {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
        </button>
      </form>
    </div>
  );
}

/* ---------- Feed tab ---------- */
function FeedTab({ gid }) {
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/groups/${gid}/posts`).then(r => setPosts(r.data || [])).catch(()=>{}), [gid]);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/groups/${gid}/posts`, { text }); setText(""); load(); }
    catch (e2) { toast.error(formatErr(e2)); }
    finally { setBusy(false); }
  };
  return (
    <div className="h-full overflow-y-auto p-4" data-testid="group-feed">
      <form onSubmit={submit} className="mb-4">
        <textarea rows={2} className="input resize-none" placeholder="Share something with the group…"
          value={text} onChange={(e)=>setText(e.target.value)} data-testid="group-feed-input"/>
        <div className="text-right mt-2">
          <button type="submit" disabled={busy || !text.trim()} className="btn-primary text-sm" data-testid="group-feed-post-btn">{busy ? <Loader2 size={12} className="animate-spin"/> : "Post"}</button>
        </div>
      </form>
      {posts.length === 0 && <p className="text-center text-[#8A8A8A] py-8">No posts yet.</p>}
      <div className="space-y-3">
        {posts.map(p => (
          <div key={p.id} className="rounded-2xl border border-[#E5E5E3] p-4" data-testid={`group-post-${p.id}`}>
            <div className="flex items-center gap-2.5 mb-2">
              {p.author.avatar && <img src={p.author.avatar} alt="" className="h-8 w-8 rounded-full object-cover"/>}
              <div>
                <p className="text-sm font-medium">{p.author.name}</p>
                <p className="text-[11px] text-[#8A8A8A]">{new Date(p.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
              </div>
            </div>
            {p.text && <p className="text-sm whitespace-pre-wrap">{p.text}</p>}
            {p.image && <img src={p.image} alt="" data-lightbox className="mt-3 rounded-2xl w-full max-h-[400px] object-cover"/>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Shared Group Calendar ---------- */
function CalendarTab({ gid }) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [cursor, setCursor] = useState(startOfMonth(new Date()));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/groups/${gid}/events`);
      setEvents(data || []);
    } catch { /* */ }
  }, [gid]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const m = new Map();
    events.forEach(e => {
      if (!e.start) return;
      const k = ymd(new Date(e.start));
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    });
    return m;
  }, [events]);

  const days = buildMonthGrid(cursor);
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const todayKey = ymd(new Date());

  return (
    <div className="h-full overflow-y-auto p-4" data-testid="group-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth()-1, 1))} className="h-8 w-8 rounded-full hover:bg-[#F2F2F0] inline-flex items-center justify-center" data-testid="gcal-prev">‹</button>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth()+1, 1))} className="h-8 w-8 rounded-full hover:bg-[#F2F2F0] inline-flex items-center justify-center" data-testid="gcal-next">›</button>
          <button onClick={() => setCursor(startOfMonth(new Date()))} className="btn-ghost !py-1 text-xs" data-testid="gcal-today">Today</button>
        </div>
        <p className="font-medium">{monthLabel}</p>
        <button className="btn-primary text-xs" onClick={() => setDialog({ mode: "create", date: new Date() })} data-testid="gcal-add-btn">
          <Plus size={12}/> Add event
        </button>
      </div>
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-[#8A8A8A] mb-1">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => <div key={d} className="px-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          const key = ymd(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const evs = byDay.get(key) || [];
          return (
            <div key={i}
              onClick={() => { setDialog({ mode: "create", date: d }); }}
              role="button"
              className={`min-h-[74px] rounded-xl border p-1.5 flex flex-col gap-0.5 text-xs cursor-pointer ${inMonth ? "bg-white" : "bg-[#FAFAF8] text-[#8A8A8A]"} ${key === todayKey ? "border-[#0A0A0A]" : "border-[#E5E5E3]"}`}
              data-testid={`gcal-day-${key}`}>
              <div className="flex items-center justify-between">
                <span className={`text-[11px] font-medium ${key === todayKey ? "bg-[#0A0A0A] text-white rounded-full px-1.5" : ""}`}>{d.getDate()}</span>
              </div>
              {evs.slice(0, 3).map(e => (
                <button key={e.id}
                  onClick={(ev) => { ev.stopPropagation(); setDialog({ mode: "edit", event: e }); }}
                  className="text-[10px] px-1.5 py-0.5 rounded truncate text-left"
                  style={{ background: `${e.color}22`, color: e.color }}
                  data-testid={`gcal-ev-${e.id}`}>{e.title}</button>
              ))}
              {evs.length > 3 && <span className="text-[9px] text-[#8A8A8A]">+{evs.length - 3}</span>}
            </div>
          );
        })}
      </div>

      {dialog && (
        <GroupEventDialog
          gid={gid}
          mode={dialog.mode}
          event={dialog.event}
          defaultDate={dialog.date}
          currentUserId={user?.id}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); }}
        />
      )}
    </div>
  );
}

function GroupEventDialog({ gid, mode, event, defaultDate, currentUserId, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const seed = isEdit ? event : {};
  const d = defaultDate || new Date();
  const [f, setF] = useState({
    title: seed.title || "",
    start: seed.start ? toInput(seed.start) : dateToInput(d),
    end:   seed.end   ? toInput(seed.end)   : dateToInput(d, 60),
    notes: seed.notes || "",
    color: seed.color || "#0057FF",
    all_day: !!seed.all_day,
  });
  const [busy, setBusy] = useState(false);
  const canEdit = !isEdit || (event.author_id === currentUserId);
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const payload = { ...f, start: toISO(f.start), end: toISO(f.end || f.start) };
      if (isEdit) await api.patch(`/groups/${gid}/events/${event.id}`, payload);
      else await api.post(`/groups/${gid}/events`, payload);
      toast.success(isEdit ? "Event updated" : "Event added");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!isEdit || !confirm("Delete this event?")) return;
    setBusy(true);
    try { await api.delete(`/groups/${gid}/events/${event.id}`); toast.success("Deleted"); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="group-event-dialog">
      <div onClick={(e)=>e.stopPropagation()} className="bg-white rounded-3xl border border-[#E5E5E3] max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="overline">Group calendar</p>
            <h3 className="text-xl font-heading mt-1">{isEdit ? "Edit event" : "Add event"}</h3>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-[#F2F2F0] inline-flex items-center justify-center"><X size={16}/></button>
        </div>
        <div className="space-y-3">
          <input className="input" placeholder="Title" value={f.title} onChange={(e)=>setF({...f,title:e.target.value})} data-testid="gcal-event-title" autoFocus/>
          <div className="grid grid-cols-2 gap-2">
            <input type="datetime-local" className="input" value={f.start} onChange={(e)=>setF({...f,start:e.target.value})} data-testid="gcal-event-start"/>
            <input type="datetime-local" className="input" value={f.end} onChange={(e)=>setF({...f,end:e.target.value})} data-testid="gcal-event-end"/>
          </div>
          <textarea rows={2} className="input resize-none" placeholder="Notes (optional)" value={f.notes} onChange={(e)=>setF({...f,notes:e.target.value})} data-testid="gcal-event-notes"/>
          <div className="flex items-center gap-3">
            <input type="color" value={f.color} onChange={(e)=>setF({...f,color:e.target.value})} className="w-7 h-7 border-0 rounded" data-testid="gcal-event-color"/>
            <label className="inline-flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={f.all_day} onChange={(e)=>setF({...f,all_day:e.target.checked})} data-testid="gcal-event-allday"/>
              All-day
            </label>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-2">
          {isEdit && canEdit && <button onClick={del} className="btn-ghost text-sm text-[#B84B4B]" data-testid="gcal-event-delete"><Trash2 size={12}/> Delete</button>}
          <button onClick={onClose} className="btn-ghost text-sm ml-auto">Cancel</button>
          {(!isEdit || canEdit) && (
            <button onClick={save} disabled={busy} className="btn-primary" data-testid="gcal-event-save">
              {busy ? <Loader2 size={14} className="animate-spin"/> : (isEdit ? "Save" : "Add")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Tools tab ---------- */
function ToolsTab({ gid }) {
  const [state, setState] = useState({ loading: true, rentals: [], services: [], orders: [] });
  useEffect(() => {
    api.get(`/groups/${gid}/tools/bookings`)
      .then(r => setState({ loading: false, ...r.data }))
      .catch(() => setState({ loading: false, rentals: [], services: [], orders: [] }));
  }, [gid]);
  return (
    <div className="h-full overflow-y-auto p-4 space-y-5" data-testid="group-tools">
      {state.loading && <p className="text-sm text-[#8A8A8A]"><Loader2 size={14} className="animate-spin inline"/> Loading…</p>}
      <ToolSection label="Active rentals" testid="tools-rentals" items={state.rentals}
        renderItem={(r) => (
          <>
            <p className="font-medium truncate">{r.listing_title || "Rental"}</p>
            <p className="text-xs text-[#8A8A8A]">{r.owner?.name || "Owner"} → {r.renter?.name || "Renter"} · {new Date(r.start).toLocaleDateString()} → {new Date(r.end).toLocaleDateString()}</p>
          </>
        )}/>
      <ToolSection label="Service bookings" testid="tools-services" items={state.services}
        renderItem={(b) => (
          <>
            <p className="font-medium truncate">{b.service_title || "Service"}</p>
            <p className="text-xs text-[#8A8A8A]">{b.customer?.name || "Customer"} · {b.hooman?.name || "Provider"} · {b.payment_status || b.status}</p>
          </>
        )}/>
      <ToolSection label="Product orders" testid="tools-orders" items={state.orders}
        renderItem={(o) => (
          <>
            <p className="font-medium truncate">{o.product_title || "Order"}</p>
            <p className="text-xs text-[#8A8A8A]">{o.buyer?.name || "Buyer"} → {o.seller?.name || "Seller"} · ${o.amount} · {o.payment_status || o.status}</p>
          </>
        )}/>
    </div>
  );
}
function ToolSection({ label, items, renderItem, testid }) {
  return (
    <div data-testid={testid}>
      <p className="overline mb-2">{label} · {items?.length || 0}</p>
      {(!items || items.length === 0) && <p className="text-xs text-[#8A8A8A]">Nothing active.</p>}
      <ul className="space-y-1.5">
        {(items || []).map(it => (
          <li key={it.id} className="rounded-xl border border-[#E5E5E3] p-3 bg-white">
            {renderItem(it)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- Members ---------- */
function MembersTab({ gid }) {
  const [ms, setMs] = useState([]);
  useEffect(() => { api.get(`/groups/${gid}/members`).then(r => setMs(r.data || [])).catch(()=>{}); }, [gid]);
  return (
    <div className="h-full overflow-y-auto p-4" data-testid="group-members">
      <ul className="divide-y divide-[#F2F2F0]">
        {ms.map(m => (
          <li key={m.user_id} className="flex items-center gap-3 py-2.5" data-testid={`member-${m.user_id}`}>
            {m.user.avatar && <img src={m.user.avatar} alt="" className="h-9 w-9 rounded-full object-cover"/>}
            <Link to={`/u/${m.user_id}`} className="flex-1 min-w-0 font-medium text-sm hover:underline truncate">{m.user.name}</Link>
            {m.user.category && <span className="text-xs text-[#8A8A8A] hidden sm:inline">{m.user.category}</span>}
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${m.role === "owner" ? "bg-[#0A0A0A] text-white" : m.role === "admin" ? "bg-[#EEF3FF] text-[#0057FF]" : "bg-[#F2F2F0] text-[#4A4A4A]"}`}>{m.role}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- Manage ---------- */
function ManageTab({ g, onChanged }) {
  const [reqs, setReqs] = useState([]);
  const load = useCallback(() => api.get(`/groups/${g.id}/join_requests`).then(r => setReqs(r.data || [])), [g.id]);
  useEffect(() => { load(); }, [load]);
  const decide = async (rid, action) => {
    try { await api.post(`/groups/${g.id}/join_requests/${rid}/${action}`); toast.success(action==="approve" ? "Approved" : "Rejected"); load(); onChanged(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const del = async () => {
    if (!confirm(`Delete "${g.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/groups/${g.id}`); toast.success("Group deleted"); window.location.href = "/groups"; }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="h-full overflow-y-auto p-4 space-y-4" data-testid="group-manage">
      <div className="rounded-2xl border border-[#E5E5E3] p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="overline">Pending join requests</p>
          <span className="text-xs text-[#8A8A8A]">{reqs.length}</span>
        </div>
        {reqs.length === 0 && <p className="text-sm text-[#8A8A8A]">No pending requests.</p>}
        <ul className="space-y-2">
          {reqs.map(r => (
            <li key={r.id} className="flex items-center gap-3 p-2 rounded-xl bg-[#FAFAF8]" data-testid={`req-${r.id}`}>
              {r.user.avatar && <img src={r.user.avatar} alt="" className="h-9 w-9 rounded-full object-cover"/>}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{r.user.name}</p>
                {r.message && <p className="text-xs text-[#4A4A4A] truncate">{r.message}</p>}
              </div>
              <button onClick={()=>decide(r.id, "approve")} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`req-approve-${r.id}`}><Check size={12}/> Approve</button>
              <button onClick={()=>decide(r.id, "reject")} className="btn-ghost !py-1.5 !px-3 text-xs text-[#B84B4B]" data-testid={`req-reject-${r.id}`}><X size={12}/> Reject</button>
            </li>
          ))}
        </ul>
      </div>
      {g.is_owner && (
        <div className="rounded-2xl border border-[#E5E5E3] p-4">
          <p className="overline">Danger zone</p>
          <p className="text-sm text-[#4A4A4A] mt-2">Deleting removes all posts, chat, and members. This cannot be undone.</p>
          <button onClick={del} className="btn-ghost text-sm text-[#B84B4B] mt-2" data-testid="group-delete-btn"><Trash2 size={12}/> Delete group</button>
        </div>
      )}
    </div>
  );
}

/* ---------- Invite dialog ---------- */
function InviteDialog({ gid, onClose }) {
  const [tab, setTab] = useState("search");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [invited, setInvited] = useState({});
  const [busy, setBusy] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/users?q=${encodeURIComponent(q)}&limit=8&is_hooman=false`)
        .then(r => setResults(r.data || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const invite = async (uid) => {
    try {
      await api.post(`/groups/${gid}/invite`, { user_id: uid });
      setInvited(v => ({ ...v, [uid]: true }));
      toast.success("Invited");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const genLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${gid}/invite_link`);
      setLinkUrl(data.url);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(linkUrl).then(() => toast.success("Link copied"));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="invite-dialog">
      <div onClick={(e)=>e.stopPropagation()} className="bg-white rounded-3xl border border-[#E5E5E3] max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="overline">Grow the group</p>
            <h3 className="text-xl font-heading mt-1">Invite members</h3>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-[#F2F2F0] inline-flex items-center justify-center"><X size={16}/></button>
        </div>
        <div className="flex gap-1 border-b border-[#E5E5E3] mb-4">
          <TabBtn active={tab==="search"} onClick={()=>setTab("search")} icon={<Search size={12}/>} label="Search users" testid="invite-tab-search"/>
          <TabBtn active={tab==="link"}   onClick={()=>setTab("link")}   icon={<Link2 size={12}/>}  label="Invite link" testid="invite-tab-link"/>
        </div>

        {tab === "search" && (
          <div>
            <input className="input" placeholder="Type a name…" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="invite-search-input" autoFocus/>
            <ul className="mt-3 space-y-1 max-h-72 overflow-y-auto">
              {results.length === 0 && q && <li className="text-xs text-[#8A8A8A] py-2">No matches.</li>}
              {results.map(u => (
                <li key={u.id} className="flex items-center gap-2 p-2 rounded-xl hover:bg-[#FAFAF8]" data-testid={`invite-result-${u.id}`}>
                  {u.avatar && <img src={u.avatar} alt="" className="h-8 w-8 rounded-full object-cover"/>}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-[11px] text-[#8A8A8A] truncate">{u.category || u.location}</p>
                  </div>
                  {invited[u.id]
                    ? <span className="text-xs text-[#0057FF]"><Check size={12} className="inline"/> Invited</span>
                    : <button onClick={()=>invite(u.id)} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`invite-btn-${u.id}`}>Invite</button>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {tab === "link" && (
          <div className="space-y-3" data-testid="invite-link-panel">
            <p className="text-sm text-[#4A4A4A]">Generate a shareable invite link. Anyone with the link can join
              {" "}(paid groups still need a subscription).</p>
            {!linkUrl && (
              <button onClick={genLink} disabled={busy} className="btn-primary w-full" data-testid="invite-link-generate">
                {busy ? <Loader2 size={14} className="animate-spin"/> : <><Link2 size={14}/> Generate link</>}
              </button>
            )}
            {linkUrl && (
              <div className="flex items-center gap-2" data-testid="invite-link-generated">
                <input readOnly value={linkUrl} className="input flex-1 !text-xs"/>
                <button onClick={copy} className="btn-ghost !py-2 !px-3" data-testid="invite-link-copy"><Copy size={14}/></button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Date helpers                                                       */
/* ------------------------------------------------------------------ */
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function buildMonthGrid(cursor) {
  const first = startOfMonth(cursor);
  const startWeekday = (first.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(first);
    d.setDate(first.getDate() - startWeekday + i);
    days.push(d);
  }
  return days;
}
function dateToInput(d, addMin = 0) {
  const dt = new Date(d.getTime() + addMin * 60000);
  const p = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${p(dt.getMonth()+1)}-${p(dt.getDate())}T${p(dt.getHours())}:${p(dt.getMinutes())}`;
}
function toInput(iso) { try { return dateToInput(new Date(iso)); } catch { return ""; } }
function toISO(v) {
  if (!v) return null;
  return v.length === 10 ? new Date(v + "T09:00:00").toISOString() : new Date(v).toISOString();
}
